import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, Button, Chip, Alert, CircularProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar,
  MenuItem, Select, Card, CardContent, CardActions, Tabs, Tab, IconButton, FormControl, InputLabel, Radio, RadioGroup, FormControlLabel,
  useMediaQuery, useTheme, Collapse, List, ListItem, ListItemText, Divider, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AccountBalance, CreditCard, Money } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { paymentsAPI, complaintsAPI, usersAPI } from '../services/api';
import FacilityBooking from './FacilityBooking';
import CCAvPaymentDialog from '../components/CCAvPaymentDialog';
import CCAvPaymentCard from '../components/CCAvPaymentCard';

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const ProfileSection = () => {
  const { user } = useAuth();
  const attrs = user?.attributes || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper 
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'center' }, 
        gap: { xs: 2, sm: 3 },
        textAlign: { xs: 'center', sm: 'left' },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }} 
      elevation={3}
    >
      <Avatar 
        sx={{ 
          width: { xs: 56, sm: 64 }, 
          height: { xs: 56, sm: 64 }, 
          bgcolor: 'primary.main', 
          fontSize: { xs: 28, sm: 32 }
        }}
      >
        {attrs.given_name ? attrs.given_name.charAt(0).toUpperCase() : '?'}
      </Avatar>
      <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            mb: { xs: 1, sm: 2 }
          }}
        >
          Profile
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: { xs: 0.5, sm: 0.75 }
        }}>
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Name:</b> {attrs.given_name} {attrs.family_name}
          </Typography>
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Email:</b> {attrs.email}
          </Typography>
          {attrs.phone_number && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Phone:</b> {attrs.phone_number}
            </Typography>
          )}
          {attrs['custom:flat_id'] && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Flat ID:</b> {attrs['custom:flat_id']}
            </Typography>
          )}
          {attrs['custom:building_id'] && (
            <Typography sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              wordBreak: 'break-word',
              overflow: 'hidden'
            }}>
              <b>Building ID:</b> {attrs['custom:building_id']}
            </Typography>
          )}
          <Typography sx={{ 
            fontSize: { xs: '14px', sm: '16px' },
            wordBreak: 'break-word',
            overflow: 'hidden'
          }}>
            <b>Role:</b> {Array.isArray(user.groups) ? user.groups.join(', ') : user.groups || 'No role assigned'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const CreateComplaintDialog = ({ open, onClose, flatId, buildingId }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const attrs = user?.attributes || {};
  const [form, setForm] = useState({
    userId: user?.userId || '',
    title: '',
    description: '',
    category: '',
    priority: 'MEDIUM',
    status: 'OPEN',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  
  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        userId: user?.userId || '',
        title: '',
        description: '',
        category: '',
        priority: 'MEDIUM',
        status: 'OPEN',
      });
      setError('');
    }
  }, [open, user?.userId]);
  
  const createMutation = useMutation((data) => complaintsAPI.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('complaints');
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || 'Failed to create complaint');
    },
  });
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Use the passed flatId prop, fallback to user's custom flat_id if not provided
    const complaintFlatId = flatId || attrs['custom:flat_id'];
    
    if (!complaintFlatId) {
      setError('Flat ID is required to create a complaint');
      return;
    }
    
    if (!buildingId) {
      setError('Building ID is required to create a complaint');
      return;
    }
    
    createMutation.mutate({
      ...form,
      flatId: complaintFlatId,
      buildingId: buildingId,
      userId: user.username || user?.attributes?.sub,
    });
  };
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: '100%' }
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        pb: { xs: 1, sm: 2 }
      }}>
        Create Complaint
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField 
            label="Title" 
            name="title" 
            value={form.title} 
            onChange={handleChange} 
            fullWidth 
            required 
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          />
          <TextField 
            label="Description" 
            name="description" 
            value={form.description} 
            onChange={handleChange} 
            fullWidth 
            required 
            multiline 
            rows={isMobile ? 2 : 3} 
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          />
          <TextField
            label="Category"
            name="category"
            value={form.category}
            onChange={handleChange}
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          >
            <option value="">Select Category</option>
            <option value="PLUMBING">Plumbing</option>
            <option value="ELECTRICAL">Electrical</option>
            <option value="CLEANING">Cleaning</option>
            <option value="SECURITY">Security</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="OTHER">Other</option>
          </TextField>
          <TextField 
            label="Priority" 
            name="priority" 
            value={form.priority} 
            onChange={handleChange} 
            fullWidth 
            select 
            SelectProps={{ native: true }} 
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          >
            <option value="URGENT">Urgent</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </TextField>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                fontSize: { xs: '14px', sm: '16px' },
                mb: 1
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, 
        pb: { xs: 2, sm: 3 },
        gap: { xs: 1, sm: 0 }
      }}>
        <Button 
          onClick={onClose}
          size={isMobile ? "small" : "medium"}
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={createMutation.isLoading}
          size={isMobile ? "small" : "medium"}
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          {createMutation.isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};



// --- ComplaintsSection filtered by flatId ---
const ComplaintsSection = ({ flatId, buildingId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1800px)'); // Even more aggressive - force mobile layout up to 1800px
  
  const { data: complaintsResponse, isLoading, error } = useQuery(
    ['complaints', flatId],
    async () => {
      if (!flatId) return [];
      // Use the correct API to get complaints by flatId
      const res = await complaintsAPI.getByFlat(flatId);
      return Array.isArray(res.data) ? res.data : [];
    },
    { enabled: !!flatId }
  );
  const complaints = Array.isArray(complaintsResponse) ? complaintsResponse : [];
  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'error';
      case 'IN_PROGRESS': return 'warning';
      case 'RESOLVED': return 'success';
      case 'CLOSED': return 'default';
      default: return 'default';
    }
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };
  if (isLoading) return <Typography>Loading complaints...</Typography>;
  if (error) return <Alert severity="error">Failed to load complaints: {error.message}</Alert>;
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Complaints</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            // Trigger the complaint dialog from the parent component with flatId and buildingId
            const event = new CustomEvent('openComplaintDialog', { 
              detail: { flatId, buildingId } 
            });
            window.dispatchEvent(event);
          }}
          size="small"
          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
        >
          Create Complaint
        </Button>
      </Box>
      
      {complaints.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          No complaints found for this flat. Click "Create Complaint" to file your first complaint.
        </Alert>
      ) : isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {complaints.map((complaint) => (
            <Card key={complaint.complaintId} variant="outlined" sx={{ p: 1 }}>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px', flex: 1, mr: 1 }}>
                    {complaint.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Chip 
                      label={complaint.priority || 'MEDIUM'} 
                      color={getPriorityColor(complaint.priority)} 
                      size="small" 
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                    <Chip 
                      label={complaint.status || 'OPEN'} 
                      color={getStatusColor(complaint.status)} 
                      size="small" 
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                  {complaint.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: '11px' }}>
                    Category: {complaint.category}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '11px' }}>
                    Flat: {complaint.flatId}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} sx={{ 
          mb: 3,
          maxWidth: '100%',
          overflowX: 'auto'
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Flat ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.map((complaint) => (
                <TableRow key={complaint.complaintId}>
                  <TableCell>{complaint.title}</TableCell>
                  <TableCell>{complaint.description}</TableCell>
                  <TableCell>{complaint.category}</TableCell>
                  <TableCell>
                    <Chip label={complaint.priority || 'MEDIUM'} color={getPriorityColor(complaint.priority)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={complaint.status || 'OPEN'} color={getStatusColor(complaint.status)} size="small" />
                  </TableCell>
                  <TableCell>{complaint.flatId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};



const FlatsSection = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userId = user?.username || user?.attributes?.sub;
  

  
  const { data: flatsResponse, isLoading, error } = useQuery(
    ['userFlats', userId],
    async () => {
      if (!userId) {
        return [];
      }
      try {
        const result = await usersAPI.getAssociations(user?.attributes?.sub);
        return result.data || [];
      } catch (error) {
        console.error('API call failed:', error);
        console.error('Error response:', error.response?.data);
        throw error;
      }
    },
    { enabled: !!userId }
  );
  const flats = Array.isArray(flatsResponse) ? flatsResponse : [];

  // Tabs state for each flat
  const [tabIndex, setTabIndex] = useState({});

  const handleTabChange = (flatId, event, newValue) => {
    setTabIndex(prev => ({ ...prev, [flatId]: newValue }));
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );
  
  if (error) return (
    <Box>
      <Alert severity="error">Failed to load flats: {error.message}</Alert>
      <Alert severity="info" sx={{ mt: 1 }}>
        Debug Info - User ID: {userId || 'No userId found'}
        <br />
        Full error: {JSON.stringify(error, null, 2)}
      </Alert>
    </Box>
  );
  
  if (flats.length === 0) return (
    <Box>
      <Typography>No flats associated with your account.</Typography>
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Debug Information:</strong><br />
          User ID: {userId || 'No userId found'}<br />
          User object keys: {user ? Object.keys(user).join(', ') : 'No user object'}<br />
          User sub: {user?.attributes?.sub || 'No sub found'}<br />
          User username: {user?.username || 'No username found'}<br />
          Flats response: {JSON.stringify(flatsResponse)}
        </Typography>
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ 
      mb: 3, 
      mx: 'auto',
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          mb: { xs: 1, sm: 2 }
        }}
      >
        My Flats
      </Typography>
      {flats.map(flat => (
        <Accordion key={flat.flatId} sx={{ 
          mb: 1,
          '& .MuiAccordionDetails-root': {
            overflowX: 'hidden'
          }
        }}>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              px: { xs: 1, sm: 2 },
              overflowX: 'hidden',
              '& .MuiAccordionSummary-content': {
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 0 },
                overflow: 'hidden',
                minWidth: 0
              }
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                flex: 1,
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 'medium',
                mb: { xs: 0.5, sm: 0 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                minWidth: 0
              }}
            >
              Flat #{flat.flatNumber} &mdash; {flat.buildingName}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: 0.5, sm: 1 },
              overflow: 'hidden',
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiChip-root': {
                fontSize: { xs: '10px', sm: '12px' },
                height: { xs: '20px', sm: '24px' },
                maxWidth: { xs: '120px', sm: '150px' }
              }
            }}>
              <Chip label={flat.flatType} size="small" />
              <Chip label={`Area: ${flat.area} sq ft`} size="small" />
              <Chip label={`Maintenance: ₹${flat.monthlyMaintenance}`} size="small" />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            px: { xs: 1, sm: 2 },
            overflowX: 'hidden', // Prevent horizontal overflow in accordion content
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <Tabs
              value={tabIndex[flat.flatId] || 0}
              onChange={(e, v) => handleTabChange(flat.flatId, e, v)}
              sx={{ 
                mb: 2,
                '& .MuiTab-root': {
                  fontSize: { xs: '12px', sm: '14px' },
                  minWidth: { xs: 'auto', sm: 160 },
                  padding: { xs: '6px 12px', sm: '12px 16px' }
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Payments" />
              <Tab label="Complaints" />
            </Tabs>
            {((tabIndex[flat.flatId] || 0) === 0) && (
              <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                <FlatPaymentsTable flat={flat} />
              </Box>
            )}
            {((tabIndex[flat.flatId] || 0) === 1) && (
              <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                <ComplaintsSection 
                  flatId={flat.flatId || flat.flatID} 
                  buildingId={flat.buildingId || flat.buildingID}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// --- FlatPaymentsTable for payments per flat ---
const FlatPaymentsTable = ({ flat }) => {
  // Outstanding balance payment state
  const [payingOutstanding, setPayingOutstanding] = useState(false);
  const [outstandingSuccess, setOutstandingSuccess] = useState(false);
  const [outstandingError, setOutstandingError] = useState('');
  const [outstandingPaid, setOutstandingPaid] = useState(flat.outstandingBalance === 0);
  
  // Payment state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CCAvenue');
  const [paymentGateway, setPaymentGateway] = useState('CCAvenue'); // 'Razorpay', 'CCAvenue'
  const [paymentError, setPaymentError] = useState('');
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [paymentConfirmationType, setPaymentConfirmationType] = useState('');
  const [paymentConfirmationMessage, setPaymentConfirmationMessage] = useState('');
  const [ccavenuePaymentAmount, setCcavenuePaymentAmount] = useState(0);
  
  // CCAvenue payment dialog state  
  const [ccavPaymentDialogOpen, setCcavPaymentDialogOpen] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  
  // CCAvenue payment card state
  const [showCCAvCard, setShowCCAvCard] = useState(false);

  // Handle automatic page refresh after successful CCAvenue payment
  useEffect(() => {
    if (showPaymentSuccess) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000); // 2 second delay to show the success message
      
      return () => clearTimeout(timer);
    }
  }, [showPaymentSuccess]);

  // Handler for paying outstanding balance
  const queryClient = useQueryClient();
  const handlePayOutstanding = async () => {
    setOutstandingError('');
    setOutstandingSuccess(false);
    setPayingOutstanding(true);
    try {
      const currentDate = new Date();
      await paymentsAPI.create({
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID || '',
        userId: user.userId,
        amount: flat.outstandingBalance,
        paymentType: "OutstandingBalance",
        paymentMethod: "CCAvenue",
        status: "COMPLETED",
        description: "Outstanding balance payment",
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
        createdAt: currentDate.toISOString()
      });
      setOutstandingSuccess(true);
      setOutstandingPaid(true);
      
      // Wait a short time for the backend to process, then refresh
      setTimeout(async () => {
        await fetchPayments();
        setCurrentPage(1); // Reset pagination to show the new payment first
        queryClient.invalidateQueries('userFlats');
      }, 300);
    } catch (err) {
      setOutstandingError(err?.response?.data?.message || 'Payment failed');
    } finally {
      setPayingOutstanding(false);
    }
  };
  // State to show/hide payment history
  const [showHistory, setShowHistory] = useState(false);
  // --- ADDED STATE AND HANDLERS ---
  // Define fetchPayments at the top so it is in scope for all handlers - using single API call
  const fetchPayments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Validate required data
      const buildingId = flat.buildingId || flat.buildingID;
      if (!buildingId || !flatId) {
        console.warn('Missing required data for fetchPayments:', { buildingId, flatId });
        setPayments([]);
        return;
      }
      
      // Make a single API call to get all payments for the building and flat
      const response = await paymentsAPI.getByBuildingAndFlat(buildingId, flatId);
      const allPayments = Array.isArray(response.data) ? response.data : [];

      // Filter payments to last 12 months
      const now = new Date();
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      
      const filteredPayments = allPayments.filter(payment => {
        // Check if payment has year/month properties
        if (payment.year && payment.month) {
          const paymentDate = new Date(payment.year, payment.month - 1, 1);
          return paymentDate >= twelveMonthsAgo;
        }
        
        // Fallback to createdAt if year/month not available
        if (payment.createdAt) {
          const paymentDate = new Date(payment.createdAt);
          return paymentDate >= twelveMonthsAgo;
        }
        
        return false;
      });

      // Sort payments in descending order by created date (most recent first)
      const sortedPayments = filteredPayments.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.paidDate || 0);
        const dateB = new Date(b.createdAt || b.paidDate || 0);
        return dateB - dateA;
      });

      setPayments(sortedPayments);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };
  const { user } = useAuth(); // <-- Add this line at the top of the component
  const flatId = flat.flatId;
  const monthlyMaintenance = flat.monthlyMaintenance;
  // --- ADDED STATE AND HANDLERS ---
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Validate required data
        const buildingId = flat.buildingId || flat.buildingID;
        if (!buildingId || !flatId) {
          console.warn('Missing required data for fetchPayments:', { buildingId, flatId });
          setPayments([]);
          return;
        }
        
        // Make a single API call to get all payments for the building and flat
        const response = await paymentsAPI.getByBuildingAndFlat(buildingId, flatId);
        const allPayments = Array.isArray(response.data) ? response.data : [];

        // Filter payments to last 12 months (consistent with fetchPayments function)
        const now = new Date();
        const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        
        const filteredPayments = allPayments.filter(payment => {
          // Check if payment has year/month properties
          if (payment.year && payment.month) {
            const paymentDate = new Date(payment.year, payment.month - 1, 1);
            return paymentDate >= twelveMonthsAgo;
          }
          
          // Fallback to createdAt if year/month not available
          if (payment.createdAt) {
            const paymentDate = new Date(payment.createdAt);
            return paymentDate >= twelveMonthsAgo;
          }
          
          return false;
        });

        // Sort payments in descending order by created date (most recent first)
        const sortedPayments = filteredPayments.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.paidDate || 0);
          const dateB = new Date(b.createdAt || b.paidDate || 0);
          return dateB - dateA;
        });
        
        setPayments(sortedPayments);
        setCurrentPage(1); // Reset page when flat changes
      } catch (err) {
        console.error('Error in fetchPayments:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    if (flatId) fetchPayments();
  }, [flatId, flat.buildingId, flat.buildingID]);

  // Show payment history state
  const now = new Date();

  // Compose rows: show payment history + pending rows for unpaid months
  // Build last twelve months for payment history
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  const monthsToCheck = [];
  for (let i = 0; i < 12; i++) {
    monthsToCheck.unshift({ year: y, month: m });
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }
  // Map payments by year+month for quick lookup, also include all completed payments
  const paymentMap = {};
  const allCompletedPayments = [];
  
  payments.forEach(p => {
    if (p.status === 'COMPLETED') {
      allCompletedPayments.push(p);
    }
    if (p.year && p.month) {
      paymentMap[`${p.year}-${p.month}`] = p;
    }
  });
  
  // Show latest 6 completed payments, newest-first
  const sortedCompleted = [...allCompletedPayments].sort((a, b) => (Date.parse(b.createdAt || b.paidDate || 0) || 0) - (Date.parse(a.createdAt || a.paidDate || 0) || 0));
  const latestSix = sortedCompleted.slice(0, 6);
  const paymentRows = latestSix.map(payment => ({ type: 'completed', payment }));
  const totalPages = 1; // No pagination for tenant view, showing only latest 6
  
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };


  const handleRazorpayPayment = async (flat, amount, paymentMethod) => {
    try {
      setPayingOutstanding(true);
      
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setOutstandingError('Razorpay SDK failed to load. Please try again.');
        setPayingOutstanding(false);
        return;
      }
      
      const currentDate = new Date();
      const orderRes = await paymentsAPI.create({
        amount: amount, // Don't multiply by 100 - backend handles conversion
        currency: 'INR',
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        userId: user.userId,
        paymentType: "OutstandingBalance",
        paymentMethod: "RAZOR PAY",
        description: "Outstanding balance payment",
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1
      });
    const responseData = orderRes.data;
    const order = responseData.order; // Extract the order object from the response
    // Validate that building has Razorpay credentials configured
    if (!order.key_id) {
      throw new Error('Razorpay payment is not configured for this building. Please contact administrator to set up payment gateway.');
    }
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: 'Apartment Maintenance',
      description: 'Maintenance Payment',
      order_id: order.id,
      handler: async function (response) {
        try {
          await paymentsAPI.update(responseData.paymentId, {
            paymentId: responseData.paymentId,
            flatId: flat.flatId,
            status: 'COMPLETED',
            amount: amount,
            description: 'Payment completed via Razorpay',
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id,
            razorpaySignature: response.razorpay_signature,
            buildingId: flat.buildingId || flat.buildingID,
            paymentMethod
          });
          
          setOutstandingSuccess(true);
          
          // Only mark as fully paid if payment amount equals or exceeds outstanding balance
          const outstandingAmount = Number(flat.outstandingBalance || 0);
          if (amount >= outstandingAmount) {
            setOutstandingPaid(true);
          } else {
            // For partial payments, update the flat's outstanding balance
            flat.outstandingBalance = Math.max(0, outstandingAmount - amount);
            setOutstandingPaid(flat.outstandingBalance === 0);
          }
          
          await fetchPayments();
          
          // Reset pagination to first page to show the new payment
          setCurrentPage(1);
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setOutstandingSuccess(false);
          }, 5000);
          
        } catch (error) {
          console.error('Payment completion error:', error);
          setOutstandingError('Payment completed but verification failed. Please contact support.');
        }
      },
      prefill: {
        name: user.firstName + ' ' + user.lastName,
        email: user.email,
        contact: user.phoneNumber
      },
      theme: { color: "#528FF0" }
    };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setPayingOutstanding(false);
      
    } catch (error) {
      console.error('Razorpay payment error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      
      let errorMessage = 'Payment initiation failed. Please try again.';
      if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later or contact support.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setOutstandingError(errorMessage);
      setPayingOutstanding(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1800px)'); // Even more aggressive - force mobile layout up to 1800px

  // Process payment function to handle actual payment logic
  const processPayment = async () => {
    const amount = Number(paymentAmount);
    const outstandingAmount = Number(flat.outstandingBalance || 0);
    
    setPaymentError('');
    setOutstandingSuccess(false);
    setPayingOutstanding(true);
    
    try {
      let description;
      if (amount === outstandingAmount) {
        description = "Outstanding balance paid in full";
      } else {
        description = `Partial payment of ₹${amount} towards outstanding balance`;
      }
      
      const currentDate = new Date();
      await paymentsAPI.create({
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        userId: user?.username || user?.attributes?.sub,
        amount: amount,
        paymentType: 'OutstandingBalance',
        paymentMethod: paymentMethod,
        status: 'COMPLETED',
        description: description,
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
        createdAt: currentDate.toISOString()
      });
      
      setOutstandingSuccess(true);
      setPaymentError('');
      setPaymentAmount('');
      
      // Refresh payments data with a shorter delay to show cash payment immediately
      setTimeout(async () => {
        await fetchPayments();
        setCurrentPage(1); // Reset pagination to show the new payment first
        queryClient.invalidateQueries('userFlats');
      }, 300);
    } catch (error) {
      setPaymentError('Failed to process payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setPayingOutstanding(false);
    }
  };

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">Failed to load payments: {error.message}</Alert>;
  
  // Temporary debug message


  return (
    <Box>
      {/* Outstanding balance section with friendly message */}
      {flat.outstandingBalance > 0 && !outstandingPaid ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
            Outstanding Balance (incl. current month): ₹{flat.outstandingBalance}
          </Alert>
          
          {/* Payment form - hide when CCAvenue card is shown */}
          {!showCCAvCard && (
         <Box sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           gap: { xs: 1, sm: 2 }, 
           flexWrap: 'wrap',
           flexDirection: { xs: 'column', sm: 'row' },
           alignItems: { xs: 'stretch', sm: 'center' },
           width: '100%'
         }}>
            <Typography variant="body2" sx={{ 
              mr: { xs: 0, sm: 1 }, 
              fontSize: { xs: '14px', sm: '16px' },
              mb: { xs: 1, sm: 0 },
              alignSelf: 'flex-start',
              mt: { xs: 0, sm: 1 }
            }}>
              Payment Amount:
            </Typography>
            
            <TextField
              size="small"
              type="number"
              value={paymentAmount || flat.outstandingBalance.toString()}
              onChange={(e) => {
                setPaymentAmount(e.target.value);
                setPaymentError('');
              }}
              inputProps={{ 
                min: 0, 
                max: flat.outstandingBalance,
                step: 0.01
              }}
              sx={{ 
                width: { xs: '100%', sm: '150px' },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '14px', sm: '16px' }
                }
              }}
            />
            
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              {/* Cash payment option removed for Owner/Tenant dashboard */}
              <FormControlLabel
                control={
                  <Radio
                    checked={paymentMethod === 'Razorpay'}
                    onChange={() => {
                      if (!paymentAmount) {
                        setPaymentAmount(flat.outstandingBalance.toString());
                      }
                      setPaymentMethod('Razorpay');
                      setPaymentError('');
                    }}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CreditCard sx={{ fontSize: '18px', color: '#1976d2' }} />
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>Razorpay</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={paymentMethod === 'CCAvenue'}
                    onChange={() => {
                      if (!paymentAmount) {
                        setPaymentAmount(flat.outstandingBalance.toString());
                      }
                      setPaymentMethod('CCAvenue');
                      setPaymentError('');
                    }}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountBalance sx={{ fontSize: '18px', color: '#ff9800' }} />
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>CCAvenue</Typography>
                  </Box>
                }
              />
            </Box>
            
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const currentAmount = paymentAmount || flat.outstandingBalance.toString();
                const currentMethod = paymentMethod || 'CCAvenue';
                
                setPaymentError('');
                
                if (!currentAmount || isNaN(currentAmount)) {
                  setPaymentError('Please enter a valid payment amount.');
                  return;
                }
                
                const amount = Number(currentAmount);
                const outstandingAmount = Number(flat.outstandingBalance || 0);
                
                if (amount <= 0) {
                  setPaymentError('Payment amount must be greater than 0.');
                  return;
                }
                
                if (amount > outstandingAmount) {
                  setPaymentError(`Payment amount cannot exceed outstanding balance of ₹${outstandingAmount}.`);
                  return;
                }
                
                // Handle payment scenarios for all methods
                  if (amount === outstandingAmount) {
                  // Scenario A: Exact outstanding amount - proceed directly
                  if (currentMethod === 'Razorpay') {
                    handleRazorpayPayment(flat, amount, 'Online');
                  } else if (currentMethod === 'CCAvenue') {
                    setCcavenuePaymentAmount(amount);
                    setShowCCAvCard(true);
                  } else {
                    // Fallback: treat unknown method as online
                    handleRazorpayPayment(flat, amount, 'Online');
                  }
                } else {
                  // Scenario B: Less than outstanding - show confirmation
                  const remainingBalance = outstandingAmount - amount;
                  setPaymentConfirmationType('partial');
                  setPaymentConfirmationMessage(
                    `You are paying ₹${amount} against an outstanding balance of ₹${outstandingAmount}. ` +
                    `The remaining balance will be ₹${remainingBalance}. Do you want to continue?`
                  );
                  // Store current method and amount for confirmation callback
                  setPaymentMethod(currentMethod);
                  setPaymentAmount(amount.toString());
                  setShowPaymentConfirmation(true);
                }
              }}
              disabled={payingOutstanding || outstandingPaid}
              sx={{ 
                fontSize: { xs: '11px', sm: '13px' },
                minWidth: { xs: '100%', sm: '100px' },
                whiteSpace: 'nowrap',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 }
              }}
            >
              Pay Now
            </Button>
          </Box>
          )}
          
          {/* Show payment error if any */}
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '12px', sm: '14px' } }}>
              {paymentError}
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          🎉 Great! You don't have any outstanding dues for this flat.
        </Alert>
      )}
      
      {outstandingError && (
        <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          {outstandingError}
        </Alert>
      )}
      
      {outstandingSuccess && (
        <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '14px', sm: '16px' } }}>
          Payment processed successfully! Your payment will be reflected shortly.
        </Alert>
      )}

      {/* CCAvenue Payment Card - Display immediately after payment buttons */}
      {showCCAvCard && (
        <Box sx={{ mb: 3 }}>
          <CCAvPaymentCard 
            flat={flat}
            amount={ccavenuePaymentAmount || flat.outstandingBalance}
            onClose={() => {
              setShowCCAvCard(false);
              setCcavenuePaymentAmount(0); // Reset stored amount
            }}
            onPaymentSuccess={(result) => {
              console.log('CCAvenue payment completed successfully:', result);
              setShowPaymentSuccess(true);
              setOutstandingError('');
              setShowCCAvCard(false);
              setCcavenuePaymentAmount(0); // Reset stored amount
              
              // The page will auto-refresh via useEffect in parent
            }}
            onPaymentError={(error) => {
              console.log('CCAvenue payment failed:', error);
              setOutstandingError(error);
              setShowCCAvCard(false);
              setCcavenuePaymentAmount(0); // Reset stored amount
            }}
          />
        </Box>
      )}
      

      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {paymentRows.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '14px' }}>
              No completed payments found for this flat.
            </Alert>
          ) : (
            paymentRows.map((row, idx) => {
              if (row.type === 'completed') {
                const payment = row.payment;
                return (
                  <Card key={payment.paymentId} variant="outlined" sx={{ p: 1 }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                          ₹{payment.amount}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip 
                            label={payment.status} 
                            color="success" 
                            size="small" 
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                          <Chip 
                            label="Paid" 
                            color="success" 
                            size="small" 
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                        </Box>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '12px', 
                          color: 'text.secondary', 
                          mb: 1,
                          wordBreak: 'break-all',
                          lineHeight: 1.2,
                          cursor: payment.paymentId.length > 20 ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (payment.paymentId.length > 20) {
                            navigator.clipboard?.writeText(payment.paymentId);
                            // You could add a toast notification here
                          }
                        }}
                        title={payment.paymentId.length > 20 ? 'Click to copy full ID' : payment.paymentId}
                      >
                        ID: {payment.paymentId.length > 20 
                          ? `${payment.paymentId.substring(0, 20)}...` 
                          : payment.paymentId
                        }
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Type: {payment.paymentType}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '11px' }}>
                          Method: {payment.paymentMethod}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        Date: {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            })
          )}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <>
          <TableContainer component={Paper} sx={{ 
            mb: 2,
            maxWidth: '100%',
            overflowX: 'auto',
            '& .MuiTable-root': {
              width: '100%',
              maxWidth: '100%',
              tableLayout: 'fixed' // Use fixed layout for better column control
            }
          }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  width: '18%', 
                  maxWidth: '120px',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Payment ID
                </TableCell>
                <TableCell sx={{ 
                  width: '14%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Amount
                </TableCell>
                <TableCell sx={{ 
                  width: '12%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Type
                </TableCell>
                <TableCell sx={{ 
                  width: '13%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Method
                </TableCell>
                <TableCell sx={{ 
                  width: '13%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  width: '18%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Date
                </TableCell>
                <TableCell sx={{ 
                  width: '12%',
                  padding: { xs: '4px 6px', sm: '6px 8px' }
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No completed payments found for this flat.</TableCell>
                </TableRow>
              ) : (
                paymentRows.map((row, idx) => {
                  if (row.type === 'completed') {
                    const payment = row.payment;
                    return (
                      <TableRow key={payment.paymentId}>
                        <TableCell sx={{ 
                          maxWidth: { xs: '70px', sm: '100px' }, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          fontSize: { xs: '12px', sm: '14px' }
                        }}
                        onClick={() => navigator.clipboard?.writeText(payment.paymentId)}
                        title={`${payment.paymentId} (Click to copy)`}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ fontSize: '13px' }}
                          >
                            {payment.paymentId}
                          </Typography>
                        </TableCell>
                        <TableCell>₹{payment.amount}</TableCell>
                        <TableCell>{payment.paymentType}</TableCell>
                        <TableCell>{payment.paymentMethod}</TableCell>
                        <TableCell>
                          <Chip label={payment.status} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          {payment.createdAt
                            ? new Date(payment.createdAt).toLocaleDateString()
                            : ''}
                        </TableCell>
                        <TableCell>
                          <Chip label="Paid" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
        </>
      )}
      
      {/* Payment Confirmation Dialog */}
      <Dialog 
        open={showPaymentConfirmation} 
        onClose={() => setShowPaymentConfirmation(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Confirm Partial Payment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {paymentConfirmationMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentConfirmation(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowPaymentConfirmation(false);
              const amount = Number(paymentAmount);
              
              if (paymentMethod === 'Razorpay') {
                handleRazorpayPayment(flat, amount, 'Online');
              } else if (paymentMethod === 'CCAvenue') {
                setCcavenuePaymentAmount(amount);
                setShowCCAvCard(true);
              } else {
                processPayment();
              }
            }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* CCAvenue Payment Dialog */}
      <CCAvPaymentDialog
        open={ccavPaymentDialogOpen}
        onClose={() => setCcavPaymentDialogOpen(false)}
        paymentData={{
          amount: flat.outstandingBalance,
          flatId: flat.flatId,
          buildingId: flat.buildingId || flat.buildingID,
          paymentType: 'OutstandingBalance',
          description: `Outstanding balance payment for Flat ${flat.flatNumber || flat.flatId}`
        }}
        onPaymentSuccess={(result) => {
          setShowPaymentSuccess(true);
          setOutstandingError('');
          setCcavPaymentDialogOpen(false);
          
          // The CCAvPaymentDialog will handle the page refresh automatically
          // Just show a brief success message
          console.log('CCAvenue payment completed successfully:', result);
        }}
        onPaymentError={(error) => {
          setOutstandingError(error);
          setCcavPaymentDialogOpen(false);
        }}
      />
      
      {/* Payment Success Overlay */}
      {showPaymentSuccess && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <Paper
            sx={{
              padding: 4,
              textAlign: 'center',
              maxWidth: 400,
              margin: 2,
              borderRadius: 2
            }}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: 64, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" gutterBottom color="success.main">
              Payment Successful! 🎉
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your CCAvenue payment has been processed successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The page will refresh automatically to show your updated payment status.
            </Typography>
            <CircularProgress 
              size={24} 
              sx={{ mt: 2, color: 'success.main' }} 
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

const FacilityBookingCard = ({ onOpen }) => (
  <Card sx={{ minWidth: 275 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <EventAvailableIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
        <Typography variant="h6" component="div">
          Facility Booking
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Book amenities like clubhouse, gym, or other facilities available in your apartment complex.
      </Typography>
    </CardContent>
    <CardActions>
      <Button variant="contained" color="primary" onClick={onOpen}>
        Book a Facility
      </Button>
    </CardActions>
  </Card>
);

const OwnerTenantDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [complaintDialogOpen, setComplaintDialogOpen] = useState(false);
  const [facilityBookingOpen, setFacilityBookingOpen] = useState(false);
  const [currentFlatId, setCurrentFlatId] = useState(null);
  const [currentBuildingId, setCurrentBuildingId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check for payment success parameter and show success message
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('payment') === 'success') {
      setPaymentSuccess(true);
      // Remove the parameter from URL without reload
      window.history.replaceState({}, '', '/');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setPaymentSuccess(false);
      }, 5000);
    }
  }, [location.search]);

  // Listen for the custom event to open complaint dialog
  useEffect(() => {
    const handleOpenComplaintDialog = (event) => {
      const flatId = event.detail?.flatId;
      const buildingId = event.detail?.buildingId;
      setCurrentFlatId(flatId);
      setCurrentBuildingId(buildingId);
      setComplaintDialogOpen(true);
    };

    window.addEventListener('openComplaintDialog', handleOpenComplaintDialog);
    
    return () => {
      window.removeEventListener('openComplaintDialog', handleOpenComplaintDialog);
    };
  }, []);

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: { xs: '100%', sm: '100%', md: '1200px', lg: '1400px' },
      mx: 'auto',
      px: { xs: 1, sm: 2, md: 3 },
      py: { xs: 1, sm: 2 },
      overflowX: 'hidden',
      boxSizing: 'border-box',
      minWidth: 0,
      '& > *': {
        maxWidth: '100%',
        boxSizing: 'border-box'
      },
      '& .MuiPaper-root, & .MuiAccordion-root, & .MuiCard-root': {
        maxWidth: '100% !important',
        overflow: 'hidden',
        boxSizing: 'border-box'
      },
      '& .MuiTableContainer-root': {
        maxWidth: '100% !important',
        overflowX: 'auto',
        boxSizing: 'border-box'
      },
      '& .MuiTable-root': {
        maxWidth: '100% !important',
        tableLayout: 'fixed'
      },
      '& .MuiAccordionDetails-root': {
        overflowX: 'hidden',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }
    }}>
      {/* Payment Success Message */}
      {paymentSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '14px', sm: '16px' },
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 20 }} />
          🎉 Payment completed successfully! Your payment has been processed and your account has been updated.
        </Alert>
      )}
      
      <ProfileSection />
      {/* Feature Cards */}
      {/* <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FacilityBookingCard onOpen={() => setFacilityBookingOpen(true)} />
        </Grid>
        {/* You can add more feature cards here */}
      {/* </Grid> */}
      <FlatsSection />
      {/* Facility Booking Dialog - Commented out for now */}
      {/* <Dialog
        open={facilityBookingOpen}
        onClose={() => setFacilityBookingOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={() => setFacilityBookingOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            ×
          </IconButton>
          <FacilityBooking />
        </Box>
      </Dialog> */}
      <CreateComplaintDialog 
        open={complaintDialogOpen} 
        onClose={() => setComplaintDialogOpen(false)} 
        flatId={currentFlatId}
        buildingId={currentBuildingId}
      />
    </Box>
  );
};

export default OwnerTenantDashboard;
